import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import useAuthStore from '../../store/authStore';
import dashboardService from '../../services/dashboardService';
import DashboardMap from './DashboardMap';

function Dashboard() {
  const user = useAuthStore((state) => state.user);
  const [lastAdded, setLastAdded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [topBlacklist, setTopBlacklist] = useState([]);
  const [blacklistLoading, setBlacklistLoading] = useState(true);
  const [stats, setStats] = useState({
    totalThreats: 0,
    activeThreats: 0,
    resolvedThreats: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [showMoreBlacklist, setShowMoreBlacklist] = useState(false);
  const [showMoreTimeline, setShowMoreTimeline] = useState(false);
  const [error, setError] = useState(null);

  const fetchLastAdded = async () => {
    setLoading(true);
    try {
      const data = await dashboardService.getLastAdded();
      setLastAdded(data);
    } catch (error) {
      console.error('Error fetching last added items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopBlacklist = async () => {
    setBlacklistLoading(true);
    try {
      const data = await dashboardService.getTopBlacklist();
      setTopBlacklist(data);
    } catch (error) {
      console.error('Error fetching top blacklist:', error);
    } finally {
      setBlacklistLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const data = await dashboardService.getStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch dashboard statistics');
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchLastAdded();
    fetchTopBlacklist();
    fetchStats();
  }, []);

  const handleClose = () => {
    setSelectedItem(null);
  };

  // Ülke kodundan bayrak emojisi oluşturan fonksiyon
  const getCountryFlag = (countryCode) => {
    if (!countryCode) return null;
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  // Timeline görünümü
  const Timeline = () => {
    const displayCount = showMoreTimeline ? lastAdded.length : 5;

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          <button
            onClick={fetchLastAdded}
            className="p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100 transition-colors duration-150"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-4">
              <div className="h-2 bg-gray-200 rounded">
                <div className="h-2 bg-blue-500 rounded animate-pulse" style={{ width: '60%' }}></div>
              </div>
            </div>
          ) : (
            <>
              {lastAdded.slice(0, displayCount).map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 relative pl-5"
                >
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-blue-500 rounded-r"></div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="px-2 py-1 text-xs font-medium text-white bg-blue-500 rounded">
                      LOW
                    </span>
                    {item.country && (
                      <span className="text-base" title={`${item.country.country} (${item.country.countryCode})`}>
                        {getCountryFlag(item.country.countryCode)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <code className="text-sm text-gray-900 font-mono truncate block">
                      {item.host}
                    </code>
                    {item.hostnames && item.hostnames.length > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {item.hostnames[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDistanceToNow(new Date(item.lastUpdate), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              ))}
              {lastAdded.length > 5 && (
                <div className="p-3 flex justify-center">
                  <button
                    onClick={() => setShowMoreTimeline(!showMoreTimeline)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {showMoreTimeline ? 'Show Less' : 'Show More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Top Blacklist Panel
  const TopBlacklist = () => {
    const displayCount = showMoreBlacklist ? topBlacklist.length : 5;
    const sortedBlacklist = [...topBlacklist].sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Top Blacklist</h2>
          <button
            onClick={fetchTopBlacklist}
            className="p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100 transition-colors duration-150"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        
        <div className="divide-y divide-gray-200">
          {blacklistLoading ? (
            <div className="p-4">
              <div className="h-2 bg-gray-200 rounded">
                <div className="h-2 bg-red-500 rounded animate-pulse" style={{ width: '60%' }}></div>
              </div>
            </div>
          ) : (
            <>
              {sortedBlacklist.slice(0, displayCount).map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 relative pl-5"
                >
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-red-500 rounded-r"></div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 text-xs font-medium text-white bg-red-500 rounded">
                      {item.confidence}/10
                    </span>
                    {item.country && (
                      <span className="text-base" title={`${item.country.country} (${item.country.countryCode})`}>
                        {getCountryFlag(item.country.countryCode)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <code className="text-sm text-gray-900 font-mono">
                      {item.host}
                    </code>
                    {item.hostnames && item.hostnames.length > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.hostnames[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDistanceToNow(new Date(item.lastUpdate), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              ))}
              {topBlacklist.length > 5 && (
                <div className="p-3 flex justify-center">
                  <button
                    onClick={() => setShowMoreBlacklist(!showMoreBlacklist)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {showMoreBlacklist ? 'Show Less' : 'Show More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Detay modalı
  const DetailModal = () => (
    selectedItem && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs font-medium text-white rounded ${selectedItem.confidence >= 8 ? 'bg-red-500' : 'bg-blue-500'}`}>
                {selectedItem.confidence}/10
              </span>
              {selectedItem.country && (
                <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                  {selectedItem.country.country} ({selectedItem.country.countryCode})
                </span>
              )}
              <code className="text-sm text-gray-900 font-mono">
                {selectedItem.host}
              </code>
            </div>
            <button
              onClick={handleClose}
              className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors duration-150"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-4">
            <dl className="space-y-4">
              {selectedItem.hostnames && selectedItem.hostnames.length > 0 && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Hostnames</dt>
                  <dd className="mt-1">
                    {selectedItem.hostnames.map((hostname, index) => (
                      <span key={index} className="inline-block px-2 py-1 mr-2 mb-2 text-sm text-gray-600 bg-gray-100 rounded">
                        {hostname}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Type</dt>
                <dd className="mt-1 text-sm text-gray-900">{selectedItem.type}</dd>
              </div>
              
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">First Seen</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDistanceToNow(new Date(selectedItem.firstSeen), { addSuffix: true })}
                </dd>
              </div>
              
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Last Update</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDistanceToNow(new Date(selectedItem.lastUpdate), { addSuffix: true })}
                </dd>
              </div>
              
              {selectedItem.organization && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Organization</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedItem.organization}</dd>
                </div>
              )}
              
              {selectedItem.isp && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">ISP</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedItem.isp}</dd>
                </div>
              )}
              
              {selectedItem.asn && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">ASN</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedItem.asn}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    )
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.username || 'User'}</h1>
        <p className="mt-1 text-sm text-gray-500">Here's what's happening with your intelligence feeds.</p>
      </div>

      {/* Map and Stats Section */}
      <div className="flex gap-6 mb-6">
        {/* Map Section */}
        <div className="flex-1 bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Threat Map</h2>
          </div>
          <div className="h-[600px]">
            <DashboardMap />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="w-80">
          <div className="bg-white rounded-lg shadow p-4 sticky top-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
            <div className="space-y-4">
              {statsLoading ? (
                <div className="space-y-4">
                  <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Total Threats</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.totalThreats.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Active Threats</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.activeThreats.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Resolved</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.resolvedThreats.toLocaleString()}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Recent Activity */}
        <div className="w-full lg:w-3/5">
          <Timeline />
        </div>

        {/* Top Blacklist */}
        <div className="w-full lg:w-2/5">
          <TopBlacklist />
        </div>
      </div>

      <DetailModal />

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
}

export default Dashboard; 